import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import en from './locales/en.json';
import fr from './locales/fr.json';

/**
 * Supported languages
 */
export const SUPPORTED_LANGUAGES = {
    en: { name: 'English', nativeName: 'English', flag: '🇬🇧' },
    fr: { name: 'French', nativeName: 'Français', flag: '🇫🇷' },
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

/**
 * Default language
 */
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

/**
 * i18next configuration
 */
i18n
    // Detect user language
    .use(LanguageDetector)
    // Pass the i18n instance to react-i18next
    .use(initReactI18next)
    // Initialize i18next
    .init({
        // Resources (translation files)
        resources: {
            en: { translation: en },
            fr: { translation: fr },
        },

        // Default language
        fallbackLng: DEFAULT_LANGUAGE,

        // Supported languages
        supportedLngs: Object.keys(SUPPORTED_LANGUAGES),

        // Debug mode (disable in production)
        debug: import.meta.env.DEV,

        // Interpolation options
        interpolation: {
            escapeValue: false, // React already escapes values
        },

        // Language detection options
        detection: {
            // Order of language detection
            order: ['localStorage', 'navigator', 'htmlTag'],

            // Cache user language in localStorage
            caches: ['localStorage'],

            // localStorage key
            lookupLocalStorage: 'yim-language',

            // Don't check cookie
            lookupCookie: undefined,
        },

        // React options
        react: {
            useSuspense: true,
        },
    });

export default i18n;

/**
 * Helper function to change language
 */
export const changeLanguage = (lng: SupportedLanguage): Promise<void> => {
    return i18n.changeLanguage(lng) as unknown as Promise<void>;
};

/**
 * Get current language
 */
export const getCurrentLanguage = (): SupportedLanguage => {
    return i18n.language as SupportedLanguage;
};

